import React, { useContext, useEffect, useRef, useState } from "react"
import { CarCareTipsTemplate } from "./templates"
import { graphql } from "gatsby"
import { Layout } from "../components/atoms/Layout"
import { SEO } from "../components/atoms/SEO"
import { DealersNearYou } from "../components/organisms/DealersNearYou"
import useTealiumView from "../hooks/Tealium/useTealiumView"
import useTealiumNoZip from "../hooks/Tealium/useTealiumNoZip"
import useDealers from "../hooks/useDealers"
import { LocationContext } from "../contexts/Location"
import { LanguageContext } from "../contexts/Language"
import { windowExists } from "../helpers"
import CarCareHero from "../components/organisms/CarCareTips/CarCareHero"
import ArticlesGrid from "../components/organisms/CarCareTips/ArticlesGrid"
import PopularTips from "../components/organisms/CarCareTips/PopularTips"
import CarCareCouponSection from "../components/organisms/CarCareTips/CarCareCouponSection"
import FeaturedSnippetModule from "../components/organisms/FeaturedSnippet/FeaturedSnippetModule"

const CarCareTipsPage: React.FC<CarCareTipsTemplate> = ({
  data,
  location,
  pageContext,
}: any) => {
  const { page, articles, carCareSubNav, parts } = data
  const articleRoutes = data.articleRoutes.nodes.filter(
    (node: any) => node.page?._type === "page.article",
  )

  const { _ } = useContext(LanguageContext)

  //Tealium

  const { handleTealView } = useTealiumView()
  const [{ dealers }] = useContext(LocationContext)
  const pageload = useRef(true)
  const { getDealerInfo } = useDealers()
  const [isOutOfArea] = useTealiumNoZip()

  useEffect(() => {
    if (windowExists() && location?.hash?.includes("#dealersNearYou")) {
      const dealersNearYouEl = document.getElementById("dealersNearYou")
      const dealersNearYouPosition =
        dealersNearYouEl && dealersNearYouEl.getBoundingClientRect()
      dealersNearYouPosition &&
        window.scroll({
          top:
            dealersNearYouPosition.top +
            window.innerHeight * 0.85 +
            window.scrollY,
          behavior: "smooth",
        })
    }
  }, [])

  useEffect(() => {
    if (pageload.current && dealers) {
      const dealerInfo = getDealerInfo()
      const topic = pageContext.category
      const isLandingPage = topic === "all"

      // Fire only on inital page load, and not every time dealers is updated
      pageload.current = false
      handleTealView({
        tealium_event: isLandingPage ? "car_care_tips" : "car_care_topic",
        page_name: isLandingPage ? "car care tips" : topic,
        search_results: isOutOfArea ? 0 : dealerInfo.searchResults,
        dealer_code: isOutOfArea ? "NOT SET" : dealerInfo.dealerCodes,
        dealer_name: isOutOfArea ? "NOT SET" : dealerInfo.dealerNames,
        page_type: "car care tips",
        ...(!isLandingPage && { vehicle_category: topic }),
      })
    }
  }, [dealers, isOutOfArea])

  return (
    <Layout
      tealium={{
        label: "carCareTips",
        extraData:
          pageContext.category == "all"
            ? {}
            : { vehicicle_category: [pageContext.category.toLowerCase()] },
      }}
    >
      <SEO
        url={location.href}
        title={page.title}
        keywords={page.seo?.keywords}
        schema={page.seo?.schema}
        description={page.seo?.description}
        canonicalPath={page.seo?.canonicalPath}
      />
      <SanitySections
        data={page}
        translations={pageContext.translations}
        category={pageContext.category}
        articleRoutes={articleRoutes}
        articles={articles.nodes}
        parts={parts.nodes}
        carCareSubNav={carCareSubNav.nodes[0]}
        pathname={location.pathname}
        allCoupons={data.allCoupons.nodes}
      />
    </Layout>
  )
}

const SanitySections = ({
  data,
  category,
  translations,
  articleRoutes,
  articles,
  parts,
  carCareSubNav,
  pathname,
  allCoupons,
}: any): JSX.Element => {
  const retentionSalesEventData = data?.content?.find(
    (section: any) => section._type === "salesEventHeroPageBuilder",
  )?.salesEvent
  const retentionSalesEventHeroData = retentionSalesEventData?.salesEventActive
    ? retentionSalesEventData?.hero
    : null
  const isCarCareLandingPage = category == "all"
  const [selectedParts, setSelectedParts] = useState([])

  return (
    <>
      {data?.content?.map((section: any, index: number) => (
        <React.Fragment key={section?._key || `car-care-tips-section-${index}`}>
          {(() => {
            switch (section?._type) {
              case "carCareTipsHero":
                return (
                  <CarCareHero
                    showBackButton={!isCarCareLandingPage}
                    {...section}
                  />
                )

              case "couponsSection":
                return (
                  <CarCareCouponSection
                    {...section}
                    selectedParts={selectedParts}
                    category={category}
                    allCoupons={allCoupons}
                  />
                )
              case "articlesContentGrid":
                return (
                  <ArticlesGrid
                    {...section}
                    translations={translations}
                    category={category}
                    articles={articles}
                    articleRoutes={articleRoutes}
                    parts={parts}
                    subNav={carCareSubNav}
                    pathname={pathname}
                    isCarCareLandingPage={isCarCareLandingPage}
                    selectedParts={selectedParts}
                    setSelectedParts={setSelectedParts}
                  />
                )
              case "popularTips":
                return (
                  <PopularTips
                    {...section}
                    category={category}
                    translations={translations}
                    articleRoutes={articleRoutes}
                  />
                )
              case "dealersNearYouSection":
                return (
                  <DealersNearYou
                    type="default"
                    {...section}
                    ctaAnalyticsId="schedule service"
                    carCareTopic={category !== "all" ? category : null}
                  />
                )
              case "featuredSnippetModule":
                return <FeaturedSnippetModule {...section} />
            }
          })()}
        </React.Fragment>
      ))}
    </>
  )
}

export const query = graphql`
  query CarCareTipsPageQuery(
    $id: String
    $lang: String
    $regexCategory: String
  ) {
    page: sanityPageCarCareTips(id: { eq: $id }) {
      id
      language
      _type
      title
      seo {
        keywords
        schema
        description
        canonicalPath
      }
      selectedCategories {
        category
      }
      disclaimers {
        code
        disclaimer
      }
      content {
        ...DealersNearYouData
        ...CallToActionBoxData
        ...RetentionSalesEventImageWithTextData
        ...RetentionSalesEventHeroData
        ...CarCareHeroData
        ...ArticlesContentGridData
        ...PopularTipsData
        ...CouponsSectionData
        ...FeaturedSnippetModuleData
      }
    }
    carCareSubNav: allSanityCarCareTipsSubNavigation(
      filter: { language: { eq: $lang } }
    ) {
      nodes {
        menuItems {
          title
          titleLink {
            slug {
              current
            }
          }
          _key
        }
        menuName
      }
    }
    categories: allSanityArticleCategory {
      nodes {
        category
      }
    }
    articleRoutes: allSanityRoute {
      nodes {
        page {
          ... on SanityPageArticle {
            _type
            _id
          }
        }
        slug {
          current
        }
      }
    }
    articlesCount: allSanityPageArticle(filter: { language: { eq: $lang } }) {
      totalCount
    }
    articles: allSanityPageArticle(
      filter: {
        language: { eq: $lang }
        categories: { elemMatch: { category: { regex: $regexCategory } } }
        _id: { regex: "/^(?!.*drafts).*$/" }
      }
    ) {
      nodes {
        _id
        excerpt: _rawExcerpt(resolveReferences: { maxDepth: 5 })
        featuredImage {
          ...SanityImageData
        }
        title
        categories {
          category
        }
        part {
          name
        }
      }
    }
    parts: allSanityPagePart(filter: { language: { eq: $lang } }) {
      nodes {
        name
        language
        icon
        _id
      }
    }
    allCoupons: allSanityPageCoupon(filter: { language: { eq: $lang } }) {
      nodes {
        _id
        name
        category
        type
        expires
        offer
        image {
          ...SanityImageData
        }
        disclaimers {
          code
          disclaimer
        }
        starts
      }
    }
  }
`

export default CarCareTipsPage
